import { Component, ElementRef, computed, inject, input, output, viewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../input/input.component';
import { AuthService } from '../../core/services/auth.service';
import { Subscription, catchError, mergeMap, of, tap, throwError } from 'rxjs';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ErrorsService } from '../../core/services/errors.service';
import { LayoutService } from '../../core/services/layout.service';
import { OrderService } from '../../core/services/order.service';
import { AccountCouponsService } from '../../core/services/account/account-coupons.service';
import { Router } from '@angular/router';
import { StoreIdService } from '../../core/services/store-id.service';
import { ModalName, ModalService } from '../../core/services/modal.service';
import { ConfigService } from '../../core/services/config.service';
import { AccountResourceConfig } from '../../core/services/account/account-resource-config.model';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { GTMService } from '../../core/services/gtm.service';
import { OrderPaymentTypesService } from '../../core/services/ordering/order-payment-types.service';
import { OrderPaymentsService } from '../../core/services/ordering/order-payments.service';
import { AuditService } from '../../core/services/audit.service';
import { OrderCustomer } from '../../core/models/ordering/order-customer.model';
import { CommonModule } from '@angular/common';
import { FocusService } from '../../core/services/focus.service';
import { AriaFormDirective } from '../directives/aria-form.directive';
import { ExternalSignInComponent } from '../external-sign-in/external-sign-in.component';

@Component({
  selector: 'cwo-login-form',
  standalone: true,
  imports: [
    FormsModule,
    InputComponent,
    ReactiveFormsModule,
    AsyncButtonDirective,
    IconComponent,
    RecaptchaModule,
    CommonModule,
    TranslocoDirective,
    AriaFormDirective,
    ExternalSignInComponent
  ],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
})
export class LoginFormComponent {
  private focusService = inject(FocusService);
  authService = inject(AuthService);
  errorsService = inject(ErrorsService);
  layoutService = inject(LayoutService);
  orderService = inject(OrderService);
  configService = inject(ConfigService);
  accountCouponsService = inject(AccountCouponsService);
  storeId = inject(StoreIdService).storeId;
  gtmService = inject(GTMService);
  orderPaymentTypesService = inject(OrderPaymentTypesService);
  orderPaymentsService = inject(OrderPaymentsService);
  translocoService = inject(TranslocoService);
  private auditService = inject(AuditService);
  storeIdService = inject(StoreIdService);

  hideGuestButton = input(false);
  hasNewCreditCard = input<boolean>(false);
  showTableNumberInput = input(false);
  showTableNumberText = input(false);
  tableNumber = input('');
  showTableNumberError = input(false);
  creditCards = computed(() =>  this.accountState().account?.creditCards ?? [])
  accountState = computed(() => {
    return this.authService.state();
  });
  hasExternalSignIn = computed(() => !!this.configService.configState().store?.hasExternalSignIn);

  loadingSubscription: Subscription | null = null;
  showErrors = false;
  iconNameType = IconNameType;
  allErrors?: string[] = [];
  accountResourceConfig?: AccountResourceConfig;
  loginForm = new FormGroup({
    email: new FormControl(
      '',
      Validators.compose([Validators.required, Validators.email])
    ),
    password: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(64),
      ])
    ),
    recaptcha: new FormControl()

  });

  get email() {
    return this.loginForm.controls.email;
  }
  get password() {
    return this.loginForm.controls.password;
  }

  get recaptcha() {
    return this.loginForm.controls.recaptcha;
  }

  validationMessages: any = {
    email: {
      required: 'Email is required',
      email: 'Email is invalid',
    },
    password: {
      required: 'Password is required',
      maxlength: 'Password must be between 5 and 64 characters.', //same name as the this.control.error{} so we automatically render the messages on logAuditErrMsg
      minlength: 'Password must be between 5 and 64 characters.',
    },
    recaptcha: {
      required: 'Recaptcha is required',
    },
    tableNumber: {
      required: 'Table Number is required.',
    },
  };

  readonly forgotPasswordClicked = output<string>();
  readonly guestCheckoutClicked = output<string>();
  readonly personalDetailsCompleted = output();
  readonly tableNumberChange = output<string>();
  readonly tableNumberBlur = output();

  readonly recaptchaView = viewChild(RecaptchaComponent);

  private router = inject(Router);

  constructor() {

  }

  onForgotClicked() {
    this.focusService.savePreviousFocus();
    this.auditService.createAudit(() => "I forgot my password button clicked")
    this.forgotPasswordClicked.emit('Forgot Password');
  }

  enableErrors() {
    this.showErrors = true;
  }

  modalService = inject(ModalService);

  readonly formEl = viewChild.required<ElementRef<HTMLFormElement>>('formEl');

  onSubmit() {
    this.auditService.createAudit(() => "Sign In button was clicked")
    this.showErrors = false;
    if (!this.loginForm.valid) {
      this.auditService.sendNativeFormErrorsToAudits(this.loginForm, this.validationMessages, 'Sign In');
      this.showErrors = true;
      return;
    }
    this.allErrors = [];
    if (this.loginForm.value.email && this.loginForm.value.password) {
      this.loadingSubscription = this.authService
        .login(this.loginForm.value.email, this.loginForm.value.password, this.recaptcha.value ?? undefined)
        .pipe(
          catchError(error =>
            this.iprsCheck().pipe(
              tap(newArc => {
                this.accountResourceConfig = newArc;
                if(this.accountResourceConfig?.usesRecaptchaValidation) {
                  this.loginForm.controls['recaptcha'].setValidators(Validators.required);
                  this.loginForm.controls['recaptcha'].updateValueAndValidity();
                }
              }),
              mergeMap(() => throwError(() => error))
            )
          ),
          tap(() => {
            this.personalDetailsCompleted.emit();
          }),
          mergeMap(() => this.orderService.fetchOrder()),
          tap(() => this.accountCouponsService.reloadCoupons())
        )
        .subscribe({
          next: () => {
            this.gtmService.pushLoginEvent();
            this.loadingSubscription = null;
            
            this.auditService.createAudit(() => `User ${this.loginForm.value.email} has logged in`);
            
            if(this.orderService.order().customer) this.orderService.auditOrderCustomerUpdate(this.orderService.order().customer as OrderCustomer)

            this.modalService.closeModal(ModalName.SignIn).subscribe({
              next: (state) => {
                if(state?.attemptedRoute) {
                  this.router.navigate([...this.storeIdService.getRoutePathSegments([state?.attemptedRoute])]);
                }
              } 
            });
          },
          error: (err) => {
            this.recaptchaView()?.reset();
            this.loadingSubscription = null;
            if (!err.error) {
              this.allErrors = ['Network error'];
            } else {
              const parsedError = this.errorsService.parseErrorResponse(err);
              this.allErrors = parsedError.allErrors;
              this.auditService.createAudit(() => `Login failed for user with email: ${this.loginForm.value.email} with error: ${parsedError.allErrors}`);
            }
          },
        });
    }
  }

  protected iprsCheck() {
    return this.configService.getAccountResourceConfig().pipe(catchError(() => of(this.accountResourceConfig)));
  }

  public recaptchaResolved(value: string | null): void {
    this.recaptcha.setValue(value);
  }

  handleContinueAsGuest() {
    this.auditService.createAudit(() => "\"Continue as Guest\" button clicked.");
    this.modalService.closeModal();
    this.guestCheckoutClicked.emit('Guest Checkout');
  }
}
